import React, { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Button, Pagination, Modal } from 'react-bootstrap';
import { getNewsletterHistory } from 'helpers/http/common';
import toast from 'react-hot-toast';
import moment from 'moment';
import { Eye } from 'lucide-react';
import NewsletterAnalytics from './NewsletterAnalytics';
import Loader from 'components/Loader';
import NoDataFound from 'components/ui/NoDataFound';

interface NewsletterHistoryItem {
  newsletter_id: string;
  subject: string;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  status: 'pending' | 'sending' | 'sent' | 'failed';
  sent_by: string;
  date_created: string;
  sent_at: string | null;
}

interface NewsletterHistoryProps {
  onViewDetails?: (newsletterId: string) => void;
}

const NewsletterHistory: React.FC<NewsletterHistoryProps> = ({
  onViewDetails,
}) => {
  const [newsletters, setNewsletters] = useState<NewsletterHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedNewsletterId, setSelectedNewsletterId] = useState<
    string | null
  >(null);

  // fetchHistory must be stable for useEffect to work without eslint-disable
  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getNewsletterHistory(page, 20);
      setNewsletters(data.newsletters || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching newsletter history:', error);
      toast.error('Failed to load newsletter history');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchHistory();
  }, [page, fetchHistory]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      sent: 'success',
      sending: 'warning',
      failed: 'danger',
      pending: 'secondary',
    };
    return (
      <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>
    );
  };

  const getSuccessRate = (sent: number, total: number) => {
    if (total === 0) return '0%';
    return `${((sent / total) * 100).toFixed(1)}%`;
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0 fw-600">Newsletter History</h5>
        {total > 0 && (
          <Badge bg="light" text="dark">
            Total: {total}
          </Badge>
        )}
      </div>

      {loading ? (
        <Loader />
      ) : newsletters.length === 0 ? (
        <NoDataFound />
      ) : (
        <>
          <div className="table-responsive">
            <Table hover>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Recipients</th>
                  <th>Sent</th>
                  <th>Failed</th>
                  <th>Success Rate</th>
                  <th>Sent Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {newsletters.map((newsletter) => (
                  <tr key={newsletter.newsletter_id}>
                    <td>
                      <div className="fw-600">{newsletter.subject}</div>
                      <small className="text-muted">
                        Created:{' '}
                        {moment(newsletter.date_created).format(
                          'MMM DD, YYYY HH:mm'
                        )}
                      </small>
                    </td>
                    <td>{getStatusBadge(newsletter.status)}</td>
                    <td>{newsletter.recipient_count.toLocaleString()}</td>
                    <td className="text-success fw-600">
                      {newsletter.sent_count.toLocaleString()}
                    </td>
                    <td className="text-danger">
                      {newsletter.failed_count > 0
                        ? newsletter.failed_count.toLocaleString()
                        : '-'}
                    </td>
                    <td>
                      <Badge bg="info">
                        {getSuccessRate(
                          newsletter.sent_count,
                          newsletter.recipient_count
                        )}
                      </Badge>
                    </td>
                    <td>
                      {newsletter.sent_at
                        ? moment(newsletter.sent_at).format(
                            'MMM DD, YYYY HH:mm'
                          )
                        : '-'}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() =>
                          setSelectedNewsletterId(newsletter.newsletter_id)
                        }
                      >
                        <Eye size={14} className="me-1" />
                        View Analytics
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.First
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                />
                <Pagination.Prev
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                />
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 ||
                      p === totalPages ||
                      (p >= page - 2 && p <= page + 2)
                  )
                  .map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && (
                        <Pagination.Ellipsis />
                      )}
                      <Pagination.Item
                        active={p === page}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </Pagination.Item>
                    </React.Fragment>
                  ))}
                <Pagination.Next
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                />
                <Pagination.Last
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Analytics Modal */}
      <Modal
        show={selectedNewsletterId !== null}
        onHide={() => setSelectedNewsletterId(null)}
        size="lg"
        aria-labelledby="analytics-modal-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="analytics-modal-title">
            Newsletter Analytics
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedNewsletterId && (
            <NewsletterAnalytics
              newsletterId={selectedNewsletterId}
              onClose={() => setSelectedNewsletterId(null)}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default NewsletterHistory;
